import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type {
  ShipmentPaymentItem,
  ShipmentPaymentTotals,
} from '#/entities/shipment'
import { PaymentRowActions } from '#/features/payment-actions'
import { Button } from '#/shared/ui/button'
import { PlusIcon } from '#/shared/ui/icons'
import { PaymentEditor } from '#/shared/ui/payment-editor'
import type { PaymentRow } from '#/shared/ui/payment-editor'
import { Skeleton } from '#/shared/ui/skeleton'

import type { PanelState } from './panel-state'
import {
  paymentFlowLabel,
  paymentFlowShortLabel,
  paymentTitle,
  toPaymentBreakdown,
} from './payment-mappers'

type Translator = (key: string) => string

function toPaymentRow(
  item: ShipmentPaymentItem,
  t: Translator,
  shipmentId: string | undefined,
  short?: boolean,
): PaymentRow {
  return {
    id: item.id,
    direction: item.arrow,
    directionVariant: item.arrowVariant,
    title: paymentTitle(item, t),
    amount: item.amountFormatted,
    flow: {
      label: short ? paymentFlowShortLabel(item, t) : paymentFlowLabel(item, t),
      variant: item.flow.variant,
    },
    status: { label: item.statusLabel, variant: item.statusVariant },
    meta: short ? undefined : item.metaLabel,
    breakdown: toPaymentBreakdown(item.breakdown, t),
    actions: <PaymentRowActions item={item} shipmentId={shipmentId} />,
  }
}

function PaymentEmpty({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex h-35 w-full flex-col items-center justify-center gap-3.75 px-3.75">
      <p className="max-w-81 text-center text-sm text-gray-500">
        {t('orderDetails.comment.payment.emptyDescription')}
      </p>
      <Button
        size="xs"
        pressEffect
        className="gap-2.5 px-3.75"
        onClick={onCreate}
      >
        <PlusIcon className="size-2.5" />
        {t('orderDetails.comment.payment.create')}
      </Button>
    </div>
  )
}

function PaymentRowSkeleton() {
  return (
    <div className="flex w-full shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex w-full items-center gap-3 px-3.5 py-3">
        <Skeleton className="size-7 shrink-0 rounded-full" />
        <div className="flex min-w-0 flex-1 flex-col gap-1.25">
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-3 w-40" />
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          <Skeleton className="h-8 w-20 rounded-md" />
          <Skeleton className="h-8 w-16 rounded-md" />
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
      </div>
      <span aria-hidden className="block h-px w-full bg-gray-200" />
      <div className="flex w-full items-start gap-4 px-3.5 py-2.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex min-w-0 flex-1 flex-col gap-1">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  )
}

function TotalsSkeleton() {
  return (
    <div className="w-full shrink-0 border-y border-gray-200 px-6 py-2.5">
      <div className="flex items-start justify-between">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex shrink-0 flex-col gap-0.5">
            <Skeleton className="h-2.5 w-16" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
    </div>
  )
}

function PaymentLoading() {
  return (
    <div className="flex max-h-125 w-full flex-col">
      <div className="flex min-h-0 w-full flex-1 flex-col gap-5 overflow-y-auto bg-gray-50 px-2.5 py-5">
        <PaymentRowSkeleton />
        <PaymentRowSkeleton />
      </div>
      <TotalsSkeleton />
      <div className="flex h-14 shrink-0 items-center justify-end gap-2.5 px-2.5">
        <Skeleton className="h-8 w-40 rounded-md" />
      </div>
    </div>
  )
}

function PaymentFilled({
  items,
  totals,
  onCreateNew,
  shipmentId,
  compact,
  onCancel,
}: {
  items: ShipmentPaymentItem[]
  totals: ShipmentPaymentTotals | undefined
  onCreateNew: () => void
  shipmentId: string | undefined
  compact?: boolean
  onCancel?: () => void
}) {
  const { t } = useTranslation()
  const rows = useMemo(
    () => items.map((item) => toPaymentRow(item, t, shipmentId, compact)),
    [items, t, shipmentId, compact],
  )
  return (
    <PaymentEditor
      compact={compact}
      onCancel={onCancel}
      payments={rows}
      totals={totals}
      onCreateNew={onCreateNew}
    />
  )
}

export function PaymentTab({
  payment,
  shipmentId,
  compact,
  onClose,
}: {
  payment: PanelState['payment']
  shipmentId: string | undefined
  compact?: boolean
  onClose?: () => void
}) {
  if (payment.isLoading) return <PaymentLoading />
  if (payment.items.length === 0) {
    return <PaymentEmpty onCreate={payment.onCreate} />
  }
  return (
    <PaymentFilled
      items={payment.items}
      totals={payment.totals}
      onCreateNew={payment.onCreate}
      shipmentId={shipmentId}
      compact={compact}
      onCancel={onClose}
    />
  )
}
